import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Activity,
  BadgeCheck,
  Blocks,
  Box,
  Briefcase,
  Building2,
  CircleHelp,
  Cloud,
  Cpu,
  Database,
  FileText,
  FolderKanban,
  Globe2,
  Layers,
  LayoutGrid,
  Loader2,
  Network,
  Pencil,
  PlusCircle,
  ShieldCheck,
  Sparkles,
  Table2,
  Tags,
  Trash2,
  UserCircle2,
  Workflow,
  ChevronDown,
  Check,
  X,
} from 'lucide-react';
import { apiService } from '../../Connections/connectionsApiService';
import ConfirmDialog from '../../Dialogs/ConfirmDialog';

export interface DataStoreGroup {
  id: string;
  uuid: string;
  name: string;
  icon: string;
  description: string;
  tenantId: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface DataModelGroupsModalProps {
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

interface GroupFormState {
  name: string;
  icon: string;
  description: string;
  status: string;
}

const defaultForm: GroupFormState = {
  name: '',
  icon: 'LayoutGrid',
  description: '',
  status: 'Active',
};

const ICON_OPTIONS = [
  { key: 'LayoutGrid', label: 'Grid', icon: LayoutGrid },
  { key: 'Database', label: 'Database', icon: Database },
  { key: 'Layers', label: 'Layers', icon: Layers },
  { key: 'FolderKanban', label: 'Kanban', icon: FolderKanban },
  { key: 'Blocks', label: 'Blocks', icon: Blocks },
  { key: 'Table2', label: 'Table', icon: Table2 },
  { key: 'Workflow', label: 'Workflow', icon: Workflow },
  { key: 'Network', label: 'Network', icon: Network },
  { key: 'Cloud', label: 'Cloud', icon: Cloud },
  { key: 'Cpu', label: 'Compute', icon: Cpu },
  { key: 'Globe2', label: 'Global', icon: Globe2 },
  { key: 'Building2', label: 'Business', icon: Building2 },
  { key: 'Briefcase', label: 'Portfolio', icon: Briefcase },
  { key: 'FileText', label: 'Document', icon: FileText },
  { key: 'Box', label: 'Package', icon: Box },
  { key: 'Tags', label: 'Tags', icon: Tags },
  { key: 'ShieldCheck', label: 'Secure', icon: ShieldCheck },
  { key: 'BadgeCheck', label: 'Verified', icon: BadgeCheck },
  { key: 'Sparkles', label: 'Smart', icon: Sparkles },
  { key: 'Activity', label: 'Activity', icon: Activity },
  { key: 'UserCircle2', label: 'Users', icon: UserCircle2 },
];

const getIconByKey = (key: string) => ICON_OPTIONS.find((option) => option.key === key)?.icon || CircleHelp;
const resolveIconKey = (icon?: string) => {
  if (!icon) return 'LayoutGrid';
  return ICON_OPTIONS.some((option) => option.key === icon) ? icon : 'LayoutGrid';
};

const DataModelGroupsModal: React.FC<DataModelGroupsModalProps> = ({
  onClose,
  onSuccess,
  onError,
}) => {
  const [groups, setGroups] = useState<DataStoreGroup[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<GroupFormState>(defaultForm);
  const [editUuid, setEditUuid] = useState<string | null>(null);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<DataStoreGroup | null>(null);
  const [iconDropdownOpen, setIconDropdownOpen] = useState(false);

  const filteredGroups = useMemo(
    () =>
      groups.filter((group) =>
        group.name?.toLowerCase().includes(search.trim().toLowerCase()),
      ),
    [groups, search],
  );

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const response = await apiService.get('/datastores/groups', {}, 'backend');
      const list = Array.isArray(response) ? response : Array.isArray(response?.groups) ? response.groups : [];
      setGroups(list);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to load groups.';
      onError(message);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const resetForm = () => {
    setForm(defaultForm);
    setEditUuid(null);
    setIconDropdownOpen(false);
  };

  const handleEdit = async (uuid: string) => {
    setLoadingEdit(true);
    try {
      const group = await apiService.get(`/datastores/groups/${uuid}`, {}, 'backend');
      setEditUuid(uuid);
      setForm({
        name: group?.name || '',
        icon: resolveIconKey(group?.icon),
        description: group?.description || '',
        status: group?.status || 'Active',
      });
      setIconDropdownOpen(false);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to load group details.';
      onError(message);
    } finally {
      setLoadingEdit(false);
    }
  };

  const buildCreatePayload = () => ({
    uuid: typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    name: form.name.trim(),
    icon: form.icon.trim(),
    description: form.description.trim(),
    status: form.status,
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name.trim()) {
      onError('Group name is required.');
      return;
    }

    setSaving(true);
    try {
      if (editUuid) {
        await apiService.put(
          `/datastores/groups/${editUuid}`,
          {
            name: form.name.trim(),
            icon: form.icon.trim(),
            description: form.description.trim(),
            status: form.status,
          },
          'backend',
        );
        onSuccess('Data model group updated successfully.');
      } else {
        await apiService.post('/datastores/groups', buildCreatePayload(), 'backend');
        onSuccess('Data model group created successfully.');
      }

      resetForm();
      fetchGroups();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        (editUuid ? 'Failed to update group.' : 'Failed to create group.');
      onError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (group: DataStoreGroup) => {
    try {
      await apiService.delete(`/datastores/groups/${group.uuid}`, 'backend');
      onSuccess('Data model group deleted successfully.');
      setGroupToDelete(null);
      fetchGroups();
      if (editUuid === group.uuid) {
        resetForm();
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to delete group.';
      onError(message);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[140] bg-black/55 backdrop-blur-sm p-4 md:p-8 flex items-start md:items-center justify-center overflow-y-auto">
      <div className="w-full max-w-6xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 dark:border-neutral-800">
          <div>
            <h2 className="text-neutral-900 dark:text-white text-lg font-medium">Data Model Groups</h2>
            <p className="text-xs text-neutral-500 dark:text-gray-400 mt-1 font-medium">Create and manage datastore model groups.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-500 dark:text-gray-400 hover:text-neutral-900 dark:hover:text-white transition-colors"
            aria-label="Close groups modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6 p-6">
          <div className="space-y-4">
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search groups..."
              className="w-full px-4 py-2.5 rounded-lg bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 text-sm font-medium text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-gray-500 focus:border-neutral-400 dark:focus:border-indigo-500 focus:ring-1 focus:ring-neutral-300 dark:focus:ring-indigo-500/50"
            />

            <div className="border border-neutral-200 dark:border-neutral-800 rounded-xl overflow-hidden">
              <div className="grid grid-cols-[2.4fr_1fr_90px] gap-3 px-4 py-3 bg-neutral-50 dark:bg-neutral-950 text-xs text-neutral-500 dark:text-gray-400 font-medium uppercase tracking-wide border-b border-neutral-200 dark:border-neutral-800">
                <span>Name</span>
                <span>Status</span>
                <span className="text-right">Actions</span>
              </div>

              {loading ? (
                <div className="py-12 flex items-center justify-center text-neutral-500 dark:text-gray-500 text-sm font-medium gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading groups...
                </div>
              ) : filteredGroups.length === 0 ? (
                <div className="py-12 text-center text-neutral-500 dark:text-gray-500 text-sm font-medium">No groups found.</div>
              ) : (
                <div className="max-h-[420px] overflow-y-auto">
                  {filteredGroups.map((group) => (
                    <div key={group.uuid} className="grid grid-cols-[2.4fr_1fr_90px] gap-3 px-4 py-3 border-b border-neutral-200 dark:border-neutral-800/80 text-sm text-neutral-700 dark:text-gray-200 hover:bg-neutral-50 dark:hover:bg-neutral-900/80 transition-colors">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-700 dark:text-gray-200">
                            {React.createElement(getIconByKey(group.icon || 'LayoutGrid'), { className: 'w-4 h-4' })}
                          </span>
                          <p className="text-neutral-900 dark:text-white font-medium truncate">{group.name}</p>
                        </div>
                        <p className="text-xs text-neutral-500 dark:text-gray-500 truncate mt-1 font-medium">{group.description || 'No description provided.'}</p>
                      </div>
                      <span className={`inline-flex items-center h-fit px-2 py-1 rounded-full text-xs font-medium border ${group.status === 'Active' ? 'text-emerald-700 dark:text-green-300 border-emerald-200 dark:border-green-500/40 bg-emerald-50 dark:bg-green-500/10' : 'text-neutral-700 dark:text-gray-300 border-neutral-300 dark:border-neutral-600 bg-neutral-100 dark:bg-neutral-800'}`}>
                        {group.status || 'Unknown'}
                      </span>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(group.uuid)}
                          className="text-neutral-500 dark:text-gray-300 hover:text-neutral-900 dark:hover:text-white"
                          title="Edit group"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setGroupToDelete(group)}
                          className="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
                          title="Delete group"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-neutral-50 dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 rounded-xl p-5">
            <h3 className="text-neutral-900 dark:text-white text-sm font-medium mb-1">
              {editUuid ? 'Edit Group' : 'Add Group'}
            </h3>
            <p className="text-xs text-neutral-500 dark:text-gray-400 mb-4 font-medium">Define the group identity and metadata fields.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs text-neutral-700 dark:text-gray-300 mb-1.5 font-medium">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                  className="w-full px-3 py-2.5 rounded-lg bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 text-sm font-medium text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-neutral-300 dark:focus:ring-white/20 focus:border-neutral-400 dark:focus:border-neutral-500"
                  placeholder="Group name"
                  required
                />
              </div>
              <div className="relative">
                <label className="block text-xs text-neutral-700 dark:text-gray-300 mb-1.5 font-medium">Icon</label>
                <button
                  type="button"
                  onClick={() => setIconDropdownOpen((prev) => !prev)}
                  className="w-full px-3 py-2.5 rounded-lg bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 text-sm font-medium text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-neutral-300 dark:focus:ring-white/20 focus:border-neutral-400 dark:focus:border-neutral-500 flex items-center justify-between"
                >
                  <span className="inline-flex items-center gap-2">
                    {React.createElement(getIconByKey(resolveIconKey(form.icon)), { className: 'w-4 h-4 text-neutral-700 dark:text-gray-200' })}
                    {ICON_OPTIONS.find((option) => option.key === form.icon)?.label || 'Select icon'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-neutral-500 dark:text-gray-400 transition-transform ${iconDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {iconDropdownOpen && (
                  <div className="absolute z-20 mt-2 w-full max-h-64 overflow-y-auto rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-xl">
                    {ICON_OPTIONS.map((option) => {
                      const IconComponent = option.icon;
                      const selected = form.icon === option.key;
                      return (
                        <button
                          key={option.key}
                          type="button"
                          onClick={() => {
                            setForm((prev) => ({ ...prev, icon: option.key }));
                            setIconDropdownOpen(false);
                          }}
                          className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                        >
                          <span className="inline-flex items-center gap-2 text-sm font-medium text-neutral-800 dark:text-gray-100">
                            <IconComponent className="w-4 h-4 text-neutral-600 dark:text-gray-300" />
                            {option.label}
                          </span>
                          {selected && <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs text-neutral-700 dark:text-gray-300 mb-1.5 font-medium">Description</label>
                <textarea
                  value={form.description}
                  onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                  className="w-full px-3 py-2.5 rounded-lg bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 text-sm font-medium text-neutral-900 dark:text-white min-h-[100px] placeholder-neutral-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-neutral-300 dark:focus:ring-white/20 focus:border-neutral-400 dark:focus:border-neutral-500"
                  placeholder="Describe this group"
                />
              </div>
              <div>
                <label className="block text-xs text-neutral-700 dark:text-gray-300 mb-1.5 font-medium">Status</label>
                <select
                  value={form.status}
                  onChange={(event) => setForm((prev) => ({ ...prev, status: event.target.value }))}
                  className="w-full px-3 py-2.5 rounded-lg bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 text-sm font-medium text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-neutral-300 dark:focus:ring-white/20 focus:border-neutral-400 dark:focus:border-neutral-500"
                >
                  <option value="Active">Active</option>
                  <option value="Disabled">Disabled</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              <div className="pt-2 flex items-center justify-end gap-2">
                {editUuid && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-neutral-700 dark:text-gray-300 border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-900"
                  >
                    Cancel Edit
                  </button>
                )}
                <button
                  type="submit"
                  disabled={saving || loadingEdit}
                  className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-neutral-900 hover:bg-neutral-700 dark:bg-white dark:text-black dark:hover:bg-gray-200 disabled:opacity-60"
                >
                  {(saving || loadingEdit) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {!saving && !loadingEdit && <PlusCircle className="w-4 h-4 mr-2" />}
                  {editUuid ? 'Update Group' : 'Create Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {groupToDelete && (
        <ConfirmDialog
          title="Delete data model group"
          message={`Are you sure you want to delete "${groupToDelete.name}"? This action cannot be undone.`}
          onYes={() => handleDelete(groupToDelete)}
          onNo={() => setGroupToDelete(null)}
          onCancel={() => setGroupToDelete(null)}
        />
      )}
    </div>,
    document.body,
  );
};

export default DataModelGroupsModal;
